<template>
    <transition name="fade">
        <div class="popup-overlay" :class="{ 'loading': loading, 'active': active || loading  }" @click.self="closePopup()">
            <transition name="fade">
                <div class="popup-main" :class="[widthClass[size], className].join(' ')" v-show="active">
                    <div class="popup-header-main">
                        <div class="popup-header">
                            <div class="popup-header-text" v-html="heading"></div>
                            <button class="popup-close" aria-label="close" @click.prevent="closePopup()">&nbsp;</button>
                        </div>
                    </div>
                    <div class="popup-content" :class="{ 'no-padding' : ! padding }">
                        <popup-renderer-data v-if="content" :viewData="content ?? null"></popup-renderer-data>
                    </div>
                    <div class="popup-footer" :class="{ submitting: this.submitting }">
                        <button
                            v-if="buttons.submit"
                            @click.prevent="handleFormSubmit()"
                            class="button rounded-lg xl:rounded-br-3xl cta"
                            :disabled="buttons.submit.disabled"
                            :class="{ '!bg-hh-grey': buttons.submit.disabled }"
                        >{{ buttons.submit.text ?? null }}</button>
                    </div>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script>

import ViewDataRenderer from '../ViewDataRenderer.vue';
import {getBoolFromAttribute} from "@/frontend-v3/Utils.ts";

export default {

    name: 'PopupGenerator',

    components: {
        'popup-renderer-data': ViewDataRenderer,
    },

    props: [],

    data() {
        return {
            defaultSize: 5,
            size: 5,
            activeReference: null,
            formReference: null,
            heading: '',
            content: '',
            active: false,
            loading: false,
            submitting: false,
            padding: true,
            buttons: {},
            className: ''
        }
    },

    created() {

    },

    mounted() {

        this.Bus.on('build-popup', (data) => {
            data.data = data.data || {};

            const reference = data.reference;
            const forceReload = getBoolFromAttribute(data.data.options?.forceReload ?? false);

            //if this popup is already loaded, just show the already loaded popup
            if (! forceReload && reference === this.activeReference) {
                this.stopLoadingAndActivate();
                return;
            }

            this.reset();

            this.startLoading();

            //set current reference
            this.activeReference = reference;

            let location = '';

            if (data.url) {
                location = data.url
            } else {
                const segments = this.formatSegmentsBase64(data.data);
                location = '/popup/' + reference + '/' + segments;
            }

            axios.get(location)
                .then(response => {

                    const data = response.data;
                    this.heading = data.header.heading;
                    this.content = data.main.content;
                    this.buildFooter(data.footer);

                    //handle any options
                    this.size = data.options && data.options.size !== undefined ? data.options.size : this.size;
                    this.padding = data.options && data.options.padding !== undefined ? data.options.padding : this.padding;
                    this.className = data.options && data.options.class !== undefined ? data.options.class : '';

                })
                .catch((error) => {
                    console.warn(error);

                    // this.$toasted.show(error.response.data.message,{
                    //     position: "top-center",
                    //     duration : 5000
                    // });
                })
                .finally(() => {

                    setTimeout(() => {
                        this.stopLoadingAndActivate();
                    }, 0); // Replace 2000 with actual API call or data fetching
                })
            ;
        });

        if (this.loading) {
            // Simulate data loading delay
            // setTimeout(() => {
            //     this.stopLoadingAndActivate();
            // }, 1500); // Replace 2000 with actual API call or data fetching

            this.stopLoadingAndActivate();
        }

        this.Bus.on('close-popup', reference => {
            if (reference === this.activeReference) {
                this.closePopup();
            }
        });

        this.Bus.on('form-submitting', reference => {
            if (reference === this.formReference) {
                this.submitting = true;
            }
        });

        this.Bus.on('form-submit-complete', reference => {
            if (reference === this.formReference) {
                this.submitting = false;
            }
        });

        this.Bus.on('form-is-replaced', reference => {
            if (reference === this.formReference) {
                this.reset();
            }
        });

        this.Bus.on('clear-popup', reference => {
            if (reference === this.activeReference) {
                this.clearContent();
            }
        });

        this.Bus.on('toggle-popup-submit', data => {
            if (data.reference === this.activeReference) {
                if (this.buttons.submit) {
                   this.buttons.submit.disabled = ! data.state;
                }
            }
        });

        this.Bus.on('set-popup-active-class', reference => {
           this.setPopupActiveClass();
        });

        this.Bus.on('set-popup-inactive-class', reference => {
            this.setPopupInactiveClass();
        });

        document.body.addEventListener('keydown', this.handleKeyDown);
    },

    methods: {

        startLoading() {
            this.loading = true;
        },

        stopLoading() {
            this.loading = false;
            this.active = true;
        },

        stopLoadingAndActivate() {
            this.stopLoading();
            this.activate();
        },

        activate() {
            this.active = true;
            this.setPopupActiveClass();
            this.Bus.emit('popup-opened', this.activeReference);
        },

        deactivate() {
            this.active = false;
            this.setPopupInactiveClass();
        },

        setPopupActiveClass() {
            document.documentElement.classList.add('popup-active');
        },

        setPopupInactiveClass() {
            document.documentElement.classList.remove('popup-active');
        },

        closePopup() {
            this.deactivate();
            this.Bus.emit('hide-flash-message');
            this.Bus.emit('popup-closed', this.activeReference);
        },

        reset() {
            this.buttons = {};
            this.size = this.defaultSize;
            this.padding = true;
        },

        clearContent() {
            this.heading = null;
            this.content = null;
            this.activeReference = null;
        },

        handleKeyDown(event) {
            if (event.key === 'Escape') {
                this.closePopup();
            }
        },

        formatSegmentsBase64(data) {
            const jsonString = JSON.stringify(data);
            const base64EncodedString = btoa(jsonString);
            return encodeURIComponent(base64EncodedString);
        },

        buildFooter(footerData) {
            this.buttons.submit = footerData.buttons.submit ?? this.buttons.submit;
            this.formReference = this.buttons?.submit?.reference ?? this.formReference;
        },

        handleFormSubmit() {
            this.Bus.emit('submit-form', this.formReference);
        }
    },

    computed: {
        widthClass() {
            return {
                1:      'max-w-xl',
                2:      'max-w-2xl',
                3:      'max-w-3xl',
                4:      'max-w-4xl',
                5:      'max-w-5xl',
                6:      'max-w-6xl',
                7:      'max-w-7xl',
                8:      'max-w-[90vw]',
                0:      '',
            };
        }
    }

};
</script>


<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease, transform 0.5s ease; /* Added transform */
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-from.popup-main,
.fade-enter-from.popup-to {
    transform: translateY(-16px);
}

</style>
